<template>
  <div
    class="report-scroll"
    :style="reportScrollStyle"
  >
    <div class="report-card">
      <div class="report-card-body">
      <div class="report-card-header">
        <div class="report-title-wrap">
          <div class="report-title-row">
            <div class="report-title-inner">
              <div class="report-title-logo-wrap">
                <img
                  class="report-title-logo"
                  :src="wxStatic('/static/image/programCode/logo.png')"
                 />
              </div>
              <span class="report-title">{{ reportPageTitle }}</span>
            </div>
          </div>
          <div v-if="!opinionMismatch" class="report-tonguo-badge report-tonguo-badge--header">
            <img
              class="report-tonguo-badge-img"
              :src="tonguoBadgeImageSrc"
             />
          </div>
        </div>
      </div>

      <div class="section section-opinion">
        <span class="section-label">{{ opinionSectionLabel }}</span>

        <!-- 不符退回：未出具鉴别报告 -->
        <div v-if="opinionMismatch" class="opinion-fallback-wrap">
          <span class="opinion-main">{{ opinionMismatchText }}</span>
          <span v-if="opinionMismatchReason" class="opinion-mismatch-reason">退回原因：{{ opinionMismatchReason }}</span>
          <span class="opinion-mismatch-desc">{{ opinionMismatchDesc }}</span>
        </div>

        <!-- 外观+液体档：分项结论齐全时双栏（与运营稿一致） -->
        <div v-else-if="showOpinionDualColumn" class="opinion-dual-row">
          <div class="opinion-dual-col">
            <div class="opinion-dual-bar" />
            <div class="opinion-dual-body">
              <span class="opinion-dual-label">外观鉴别结果：</span>
              <span class="opinion-dual-desc">{{ appearanceOpinionDesc(splitAppearanceNum) }}</span>
              <span class="opinion-dual-verdict">{{ appearanceOpinionVerdict(splitAppearanceNum) }}</span>
            </div>
          </div>
          <div class="opinion-dual-col">
            <div class="opinion-dual-bar" />
            <div class="opinion-dual-body">
              <span class="opinion-dual-label">液体鉴别结果：</span>
              <span class="opinion-dual-desc">{{ liquidOpinionDesc(splitLiquidNum) }}</span>
              <span class="opinion-dual-verdict">{{ liquidOpinionVerdict(splitLiquidNum) }}</span>
            </div>
          </div>
        </div>

        <!-- 仅外观档或历史单：与 jb-report-normal 一致，单行 opinion-main -->
        <span v-else-if="showOpinionAppearanceCard" class="opinion-main">{{ appearanceOpinionMainSingle }}</span>

        <!-- 其它未知态 -->
        <div v-else class="opinion-fallback-wrap">
          <span class="opinion-main">{{ opinionMain }}</span>
        </div>
      </div>

      <div class="opinion-section-rule" />

      <div class="section section-divider">
        <span class="section-heading">样品基本信息</span>
        <div class="sample-info-row">
          <div class="sample-info-thumb-wrap">
            <img
              v-if="typeThumb"
              class="sample-info-thumb"
              :src="typeThumb"
             />
            <div v-else class="sample-info-thumb sample-info-thumb--ph">无图</div>
          </div>
          <div class="sample-info-list">
            <div class="sample-info-block sample-info-block--row">
              <span class="sample-k">样品型号：</span>
              <div class="sample-v-wrap">
                <span class="sample-v sample-v--muted">{{ modelLinePrimary }}</span>
                <span v-if="modelLineSecondary" class="sample-v sample-v--sub">{{ modelLineSecondary }}</span>
              </div>
            </div>
            <div class="sample-info-block sample-info-block--row">
              <span class="sample-k">样品规格：</span>
              <div class="sample-v-wrap">
                <span class="sample-v sample-v--muted">{{ sampleSpecificationDisplay }}</span>
              </div>
            </div>
            <div class="sample-info-block sample-info-block--row">
              <span class="sample-k">样品批次：</span>
              <div class="sample-v-wrap">
                <span class="sample-v sample-v--muted">{{ batchDisplay }}</span>
              </div>
            </div>
            <div class="sample-info-block sample-info-block--row">
              <span class="sample-k">鉴别编号：</span>
              <div class="sample-v-wrap">
                <span class="sample-v sample-v--muted">{{ identifyNoText }}</span>
              </div>
            </div>
            <div class="sample-info-block sample-info-block--row">
              <span class="sample-k">订单编号：</span>
              <div class="sample-v-wrap">
                <span class="sample-v sample-v--muted">{{ orderNoText }}</span>
              </div>
            </div>
            <div class="sample-info-block sample-info-block--row">
              <span class="sample-k">接受样品日期：</span>
              <div class="sample-v-wrap">
                <span class="sample-v sample-v--muted">{{ formatTime(sampleReceiveAtForReport) }}</span>
              </div>
            </div>
            <div class="sample-info-block sample-info-block--row">
              <span class="sample-k">{{ reportFinishTimeLabel }}</span>
              <div class="sample-v-wrap">
                <span class="sample-v sample-v--muted">{{ formatTime(orderData.inspectionReportAt) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="section section-divider sample-realpicture-section">
        <span class="section-heading">样品图片</span>
        <div v-if="noSamplePictures" class="sample-realpicture-empty">
          <span class="sample-realpicture-empty-txt">{{ samplePictureEmptyText }}</span>
        </div>
        <div v-else>
          <!-- 首图：实物图前 4 张 -->
          <div
            v-if="sampleMainPictureList.length"
            class="sample-realpicture-row sample-realpicture-row--fit4"
          >
            <div
              v-for="(url, idx) in sampleMainPictureList"
              :key="'sp-main-' + idx"
              class="sample-realpicture-cell sample-realpicture-cell--fit4"
            >
              <img
                v-if="url"
                class="sample-realpicture-img"
                :src="url"
               />
              <div v-else class="sample-realpicture-ph" />
            </div>
          </div>
          <!-- 第二行：实物图超出部分 + 细节图 + 余量图 -->
          <div v-if="hasSampleExtraPictures" class="sample-realpicture-extra">
            <div v-if="sampleExtraPictureOverflow" class="sample-realpicture-scroll-hint">
              <span class="sample-realpicture-scroll-hint-txt"
                >共 {{ sampleExtraPictureList.length }} 张，左右滑动查看更多</span
              >
            </div>
            <div
              v-if="!sampleExtraPictureOverflow"
              class="sample-realpicture-row sample-realpicture-row--fit4"
            >
              <div
                v-for="(url, idx) in sampleExtraPictureList"
                :key="'sp-extra-' + idx"
                class="sample-realpicture-cell sample-realpicture-cell--fit4"
              >
                <img
                  v-if="url"
                  class="sample-realpicture-img"
                  :src="url"
                 />
                <div v-else class="sample-realpicture-ph" />
              </div>
            </div>
            <div
              v-else
              class="sample-realpicture-scroll"
            >
              <div class="sample-realpicture-row sample-realpicture-row--scroll">
                <div
                  v-for="(url, idx) in sampleExtraPictureList"
                  :key="'sp-extra-s-' + idx"
                  class="sample-realpicture-cell sample-realpicture-cell--fixed"
                >
                  <img
                    v-if="url"
                    class="sample-realpicture-img"
                    :src="url"
                   />
                  <div v-else class="sample-realpicture-ph" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 样品外观鉴别内容项（不符退回不展示） -->
      <div v-if="!opinionMismatch" class="section section-divider app-checklist-section">
        <span class="app-checklist-title">样品外观鉴别内容项</span>

        <div class="app-checklist-group">
          <span class="app-checklist-cat">外盒</span>
          <div class="app-checklist-grid">
            <span class="app-checklist-item">· 材质尺寸</span>
            <span class="app-checklist-item">· 工艺细节</span>
            <span class="app-checklist-item">· 批号是否与瓶身一致</span>
          </div>
        </div>

        <div class="app-checklist-group">
          <span class="app-checklist-cat">瓶身</span>
          <div class="app-checklist-grid">
            <span class="app-checklist-item">· 材质尺寸</span>
            <span class="app-checklist-item">· 工艺细节</span>
            <span class="app-checklist-item">· 喷头工艺细节</span>
            <span class="app-checklist-item">· 瓶盖工艺细节</span>
            <span class="app-checklist-item">· 其它配件</span>
          </div>
        </div>

        <div class="app-checklist-group app-checklist-group--last">
          <span class="app-checklist-cat">标签</span>
          <div class="app-checklist-grid">
            <span class="app-checklist-item">· 材质尺寸</span>
            <span class="app-checklist-item">· 工艺细节</span>
          </div>
        </div>
      </div>

      <div v-if="showInspectionChartsSection && !opinionMismatch" class="section section-divider">
        <span class="section-heading">鉴别图谱</span>
        <div class="gc-block">
          <span class="gc-subtitle">· 标样气相色谱谱图</span>
          <div
            class="gc-chart-wrap"
            :class="{ 'gc-chart-wrap--has-img': stdChartDisplaySrc }"
          >
            <img
              v-if="stdChartDisplaySrc"
              class="gc-chart-img"
              :src="stdChartDisplaySrc"
              @error="onStdChartImgError"
             />
            <div v-else class="gc-chart-ph">
              <span class="gc-chart-ph-txt">暂无谱图</span>
            </div>
          </div>
        </div>
        <div class="gc-block gc-block--last">
          <span class="gc-subtitle">· 样品鉴别图谱</span>
          <div
            class="gc-chart-wrap"
            :class="{ 'gc-chart-wrap--has-img': jbtpDisplaySrc }"
          >
            <div v-if="hideJbtp" class="gc-chart-secure">
              <span class="gc-chart-secure-txt">为保护数据安全，鉴别不通过，不提供样品谱图</span>
            </div>
            <img
              v-else-if="jbtpDisplaySrc"
              class="gc-chart-img"
              :src="jbtpDisplaySrc"
              @error="onJbtpImgError"
             />
            <div v-else class="gc-chart-ph">
              <span class="gc-chart-ph-txt">暂无谱图</span>
            </div>
          </div>
        </div>
      </div>

      <div class="report-qianming-wrap">
        <img
          class="report-qianming-img"
          :src="wxStatic('/static/image/programCode/qianMing.png')"
         />
      </div>

      <div class="section section-divider report-tips-section">
        <span class="report-tips-title">TIPS:</span>
        <span v-if="opinionMismatch" class="report-tips-line">本说明针对无法鉴别订单的处理结果，非鉴别报告，复印无效</span>
        <span v-else class="report-tips-line">本报告针对闲置交易安心购委托商品，复印无效</span>
        <span v-if="!opinionMismatch" class="report-tips-line">本报告非CMA报告，不具备法律效力，不可作为司法用途</span>
        <span class="report-tips-line">{{ reportTipsScopeLine }}</span>
        <span class="report-tips-line report-tips-line--last">如您对报告内容有疑问，请在报告出具后15日内反馈，逾期将不受理</span>
      </div>

      <div v-if="!opinionMismatch" class="section section-divider program-cta-banner">
        <div class="program-cta-inner">
          <div class="program-cta-texts">
            <span class="program-cta-title">立即体验香味研究所鉴别服务</span>
            <span class="program-cta-desc">扫描右图中二维码，立即体验专业香水鉴别服务，新用户首单免费！</span>
          </div>
          <img
            class="program-cta-code"
            :src="wxStatic('/static/image/programCode/code.jpg')"
           />
        </div>
      </div>
      </div>
      <jb-report-watermark
        :text="watermarkDisplayText"
        :tile-count="240"
        :columns="6"
      />
    </div>
    <div class="report-scroll-end-gap" />
  </div>
</template>

<script>
import JbReportWatermark from "./JbReportWatermark.vue";
import * as Inv from "@/util/secondhandInspectionOpinion.js";
import { wxStaticUrl } from "@/util/wxStaticUrl.js";
import { absReportAssetUrl } from "@/util/reportAssetUrl.js";
import { formatSpecificationDisplay } from "@/util/specCapacityCompat.js";

export default {
  name: "JbReportSecondhand",
  components: {
    JbReportWatermark,
  },
  props: {
    orderData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      /** 标样谱图加载失败时依次尝试候选 URL */
      stdChartImgIndex: 0,
      /** 样品谱图加载失败时依次尝试候选 URL */
      jbtpImgIndex: 0,
      /** downloadFile 后的本地临时路径（标样） */
      stdChartLocalSrc: "",
      /** downloadFile 后的本地临时路径（样品） */
      jbtpLocalSrc: "",
      stdChartResolveToken: 0,
      jbtpResolveToken: 0,
    };
  },
  watch: {
    orderData: {
      deep: true,
      handler() {
        this.resetChartImageState();
      },
    },
    stdChartRemoteSrc: {
      immediate: true,
      handler(url) {
        this.prefetchChartImage("std", url);
      },
    },
    jbtpRemoteSrc: {
      immediate: true,
      handler(url) {
        if (!this.hideJbtp) {
          this.prefetchChartImage("jbtp", url);
        }
      },
    },
  },
  computed: {
    /** 安心购：外观+液体 */
    isLiqAppService() {
      return Inv.isLiqAppService(this.orderData && this.orderData.inspectionServiceType);
    },
    /** 仅外观鉴别档：整段「鉴别图谱」不展示 */
    isAppOnlyService() {
      return Inv.isAppOnlyInspectionService(this.orderData);
    },
    showInspectionChartsSection() {
      return !this.isAppOnlyService;
    },
    /** 综合结论为 3：不符退回 */
    opinionMismatch() {
      return Inv.opinionMismatchFromResult(this.orderData && this.orderData.inspectionResult);
    },
    opinionMismatchText() {
      return Inv.inspectionMismatchHeadline();
    },
    opinionMismatchDesc() {
      return Inv.inspectionMismatchBodyDesc(this.orderData);
    },
    opinionMismatchReason() {
      return Inv.inspectionMismatchReasonDisplay(this.orderData);
    },
    reportPageTitle() {
      return this.opinionMismatch ? "安心购处理说明" : "香味研究所鉴别报告";
    },
    opinionSectionLabel() {
      return this.opinionMismatch ? "处理结果" : "鉴别意见";
    },
    reportFinishTimeLabel() {
      return this.opinionMismatch ? "处理时间：" : "鉴别完成时间：";
    },
    reportTipsScopeLine() {
      return this.opinionMismatch
        ? "本说明仅对该笔订单处理结果负责"
        : "本报告仅对该笔订单委托商品负责";
    },
    /** 分项外观、液体均为 1/2 时双栏展示 */
    showOpinionDualColumn() {
      return Inv.showOpinionDualColumn(this.orderData);
    },
    splitAppearanceNum() {
      return Inv.splitAppearanceNum(this.orderData);
    },
    splitLiquidNum() {
      return Inv.splitLiquidNum(this.orderData);
    },
    /** 单栏：优先分项外观，否则用综合结论 1/2 兜底 */
    singleAppearanceNum() {
      return Inv.singleAppearanceNum(this.orderData);
    },
    showOpinionAppearanceCard() {
      return Inv.showOpinionAppearanceCard(this.orderData);
    },
    /** 仅外观档：与 jb-report-normal 的 opinion-main 同款单行展示，文案同 appearanceOpinionDesc */
    appearanceOpinionMainSingle() {
      return Inv.appearanceOpinionDesc(this.singleAppearanceNum);
    },
    /** 1 真品 2 假货 3 不符退回（兜底文案） */
    opinionMain() {
      return Inv.opinionMainFromResult(this.orderData);
    },
    /** 嵌套商品的 attrJson（品牌/型号名等） */
    productAttrObj() {
      const p = this.orderData && this.orderData.product;
      return this.parseJsonField(p && p.attrJson);
    },
    /** 商品图 JSON 解析为数组（与鉴别订单 images 类似） */
    productImagesArr() {
      const p = this.orderData && this.orderData.product;
      if (!p || p.images == null) return null;
      return this.parseJsonField(p.images);
    },
    /** 报告 JSON（platform_identify_v1 等） */
    inspectionReportPayload() {
      return this.parseJsonField(
        this.orderData && this.orderData.inspectionReportJson
      );
    },
    /** 型号图：优先 xw_type.images，不用商品 listing 图 */
    typeThumb() {
      const d = this.orderData;
      if (!d) return "";
      const typeObj = d.type;
      if (typeObj && typeObj.images != null && String(typeObj.images).trim()) {
        return absReportAssetUrl(typeObj.images);
      }
      const flat = d.typeImage || d.typeImages;
      if (flat != null && String(flat).trim()) {
        return absReportAssetUrl(flat);
      }
      return "";
    },
    modelLinePrimary() {
      const d = this.orderData;
      if (!d) return "—";
      const a = this.productAttrObj;
      const b = (
        (d.brand && d.brand.name) ||
        d.brandName ||
        (a && a.brandName) ||
        ""
      ).trim();
      const t = (
        (d.type && d.type.name) ||
        d.typeName ||
        (a && a.typeName) ||
        ""
      ).trim();
      if (b && t) return `${b} ${t}`;
      const title = (d.productTitle || "").trim();
      return b || t || title || "—";
    },
    sampleSpecificationDisplay() {
      const d = this.orderData;
      if (!d) return "—";
      const text = formatSpecificationDisplay(d.specification);
      return text || "—";
    },
    modelLineSecondary() {
      const d = this.orderData;
      const a = this.productAttrObj;
      const e =
        (d && d.typeEnglishName) ||
        (d && d.type && d.type.englishName) ||
        (a && a.typeEnglishName);
      return (e && String(e).trim()) || "";
    },
    batchEmphasis() {
      const d = this.orderData;
      if (!d) return true;
      const t2 = d.type2 != null && String(d.type2) !== "" ? Number(d.type2) : null;
      if (t2 === 1) return true;
      const b = d.batchNum;
      return !b || !String(b).trim();
    },
    batchDisplay() {
      if (!this.orderData) return "—";
      return this.batchEmphasis
        ? "无（以最新批次进行比对）"
        : String(this.orderData.batchNum).trim();
    },
    /** 鉴别编号：xw_secondhand_order.identifyNo（出具报告/开始鉴别时生成） */
    identifyNoText() {
      const d = this.orderData;
      if (!d) return "—";
      let v = d.identifyNo;
      if (v == null || String(v).trim() === "") {
        v = d.identify_no;
      }
      if (v != null && String(v).trim() !== "") return String(v).trim();
      return "—";
    },
    orderNoText() {
      return Inv.reportOrderNoText(this.orderData);
    },
    /** 水印鉴别编号数值（组件内加「鉴别编号：」前缀；为「—」时不铺水印） */
    watermarkDisplayText() {
      const t = this.identifyNoText;
      if (!t || t === "—") return "";
      return t;
    },
    /** 顶栏角标：综合或分项任一项不通过即展示不通过图 */
    tonguoBadgeImageSrc() {
      const fail = Inv.reportSecondhandFailBadge(this.orderData);
      return fail
        ? wxStaticUrl("/report/jbbtg.png")
        : wxStaticUrl("/report/jbtg.png");
    },
    /** 接受样品日期：inspection_center_receive_at，无则用创建时间兜底 */
    sampleReceiveAtForReport() {
      const d = this.orderData;
      if (!d) return null;
      const v = d.inspectionCenterReceiveAt;
      if (v != null && v !== "") return v;
      return d.createTime;
    },
    /** 实物图列表 */
    realPictureList() {
      return this.collectPictureUrlsFromField(
        this.orderData && this.orderData.realpicture
      );
    },
    defectPictureList() {
      return this.collectPictureUrlsFromField(
        this.orderData && this.orderData.defectImgs
      );
    },
    remainPictureList() {
      return this.collectPictureUrlsFromField(
        this.orderData && this.orderData.remainImgs
      );
    },
    /** 样品图片：实物图 + 瑕疵图 + 余量图 */
    samplePictureList() {
      return [
        ...this.realPictureList,
        ...this.defectPictureList,
        ...this.remainPictureList,
      ];
    },
    /** 首图：实物图前 4 张 */
    sampleMainPictureList() {
      return this.realPictureList.slice(0, 4);
    },
    /** 第二行：实物图超出部分 + 细节图 + 余量图 */
    sampleExtraPictureList() {
      return [
        ...this.realPictureList.slice(4),
        ...this.defectPictureList,
        ...this.remainPictureList,
      ];
    },
    hasSampleExtraPictures() {
      return this.sampleExtraPictureList.length > 0;
    },
    noSamplePictures() {
      return !this.sampleMainPictureList.length && !this.hasSampleExtraPictures;
    },
    samplePictureEmptyText() {
      return Inv.reportSamplePictureEmptyText(this.orderData, "secondhand");
    },
    /** 第二行超过 4 张时横向滑动并展示提示 */
    sampleExtraPictureOverflow() {
      return this.sampleExtraPictureList.length > 4;
    },
    reportCharts() {
      const payload = this.inspectionReportPayload;
      return (payload && payload.charts) || {};
    },
    /** 标样谱图候选（有坐标优先；含 NoXY/坐标互备，失败再退无坐标） */
    stdChartUrlCandidates() {
      const d = this.orderData || {};
      const ch = this.reportCharts;
      const typeObj = (d.type && typeof d.type === "object" ? d.type : {}) || {};
      return this.collectSpectrumChartCandidates(
        d.atlas2,
        ch.stdCoord,
        ch.atlas2,
        typeObj.atlas2,
        d.atlas1,
        ch.stdNoCoord,
        ch.atlas1,
        typeObj.atlas1
      );
    },
    stdChartRemoteSrc() {
      const list = this.stdChartUrlCandidates;
      const i = Math.min(this.stdChartImgIndex, Math.max(0, list.length - 1));
      return list[i] || "";
    },
    stdChartDisplaySrc() {
      return this.stdChartLocalSrc || this.stdChartRemoteSrc;
    },
    jbtpUrlCandidates() {
      const d = this.orderData || {};
      const ch = this.reportCharts;
      return this.collectReportAssetCandidates(d.jbtp, ch.identify, ch.jbtp);
    },
    jbtpRemoteSrc() {
      const list = this.jbtpUrlCandidates;
      const i = Math.min(this.jbtpImgIndex, Math.max(0, list.length - 1));
      return list[i] || "";
    },
    jbtpDisplaySrc() {
      return this.jbtpLocalSrc || this.jbtpRemoteSrc;
    },
    /** 液体不通过仍展示样品谱图；仅外观鉴别档不展示 */
    hideJbtp() {
      return Inv.shouldHideSampleIdentifyChart(this.orderData);
    },
    reportImageUrl() {
      const u = this.orderData && this.orderData.inspectionReportUrl;
      return u && String(u).trim() ? String(u).trim() : "";
    },
    reportScrollStyle() {
      return {
        backgroundImage: `url("${wxStaticUrl("/static/image/report/black-background.png")}")`,
        backgroundSize: "cover",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
      };
    },
  },
  methods: {
    wxStatic(path) {
      return wxStaticUrl(path);
    },
    /** 分项结论：仅接受 1 / 2 */
    normInspectDim(v) {
      return Inv.normInspectDim(v);
    },
    appearanceOpinionDesc(n) {
      return Inv.appearanceOpinionDesc(n);
    },
    appearanceOpinionVerdict(n) {
      return Inv.appearanceOpinionVerdict(n);
    },
    liquidOpinionDesc(n) {
      return Inv.liquidOpinionDesc(n);
    },
    liquidOpinionVerdict(n) {
      return Inv.liquidOpinionVerdict(n);
    },
    parseJsonField(str) {
      if (str == null || str === "") return null;
      if (typeof str === "object") return str;
      if (typeof str !== "string") return null;
      try {
        let parsed = JSON.parse(str);
        if (typeof parsed === "string") {
          const inner = parsed.trim();
          if (inner.startsWith("{") || inner.startsWith("[")) {
            try {
              parsed = JSON.parse(inner);
            } catch (e) {
              /* 保持单层解析结果 */
            }
          }
        }
        return parsed;
      } catch (e) {
        return null;
      }
    },
    /** 解析 JSON 数组 / urls / img1～imgN 为绝对 URL 列表 */
    collectPictureUrlsFromField(raw) {
      const urls = [];
      const add = (u) => {
        const resolved = absReportAssetUrl(u);
        if (resolved && !urls.includes(resolved)) urls.push(resolved);
      };
      let obj = raw;
      if (obj != null && typeof obj !== "object") {
        obj = this.parseJsonField(obj);
      }
      if (obj == null) return urls;
      if (Array.isArray(obj)) {
        obj.forEach(add);
        return urls;
      }
      if (typeof obj !== "object") return urls;
      if (Array.isArray(obj.urls) && obj.urls.length) {
        obj.urls.forEach(add);
        return urls;
      }
      [
        "img1",
        "img2",
        "img3",
        "img4",
        "img5",
        "img6",
        "img7",
        "img8",
      ].forEach((k) => add(obj[k]));
      return urls;
    },
    /** 标样谱图：同一文件的有坐标/无坐标版本互备，且优先有坐标版 */
    collectSpectrumChartCandidates(...values) {
      const seen = new Set();
      const out = [];
      const pushWithVariants = (raw) => {
        if (raw == null || raw === "") return;
        const resolved = absReportAssetUrl(raw);
        if (!resolved) return;
        const variants = [resolved];
        if (/_NoXY\.png$/i.test(resolved)) {
          variants.unshift(resolved.replace(/_NoXY\.png$/i, ".png"));
        } else if (/\.png$/i.test(resolved) && !/_NoXY/i.test(resolved)) {
          variants.push(resolved.replace(/\.png$/i, "_NoXY.png"));
        }
        for (const v of variants) {
          if (v && !seen.has(v)) {
            seen.add(v);
            out.push(v);
          }
        }
      };
      for (const u of values) {
        pushWithVariants(u);
      }
      out.sort((a, b) => {
        const aNo = /_NoXY/i.test(a) ? 1 : 0;
        const bNo = /_NoXY/i.test(b) ? 1 : 0;
        return aNo - bNo;
      });
      return out;
    },
    collectReportAssetCandidates(...values) {
      const seen = new Set();
      const out = [];
      for (const u of values) {
        const resolved = absReportAssetUrl(u);
        if (resolved && !seen.has(resolved)) {
          seen.add(resolved);
          out.push(resolved);
        }
      }
      return out;
    },
    resetChartImageState() {
      this.stdChartImgIndex = 0;
      this.jbtpImgIndex = 0;
      this.stdChartLocalSrc = "";
      this.jbtpLocalSrc = "";
      this.stdChartResolveToken += 1;
      this.jbtpResolveToken += 1;
    },
    prefetchChartImage(kind, url) {
      const isStd = kind === "std";
      if (isStd) {
        this.stdChartLocalSrc = "";
        this.stdChartResolveToken += 1;
      } else {
        this.jbtpLocalSrc = "";
        this.jbtpResolveToken += 1;
      }
      const token = isStd ? this.stdChartResolveToken : this.jbtpResolveToken;
      void token;
      if (!url) return;
      if (
        url.startsWith("wxfile://") ||
        url.startsWith("http://tmp/") ||
        url.startsWith("https://tmp/")
      ) {
        if (isStd) this.stdChartLocalSrc = url;
        else this.jbtpLocalSrc = url;
        return;
      }
      if (isStd) this.stdChartLocalSrc = url;
      else this.jbtpLocalSrc = url;
    },
    onStdChartImgError() {
      this.stdChartLocalSrc = "";
      if (this.stdChartImgIndex < this.stdChartUrlCandidates.length - 1) {
        this.stdChartImgIndex += 1;
      }
    },
    onJbtpImgError() {
      this.jbtpLocalSrc = "";
      if (this.jbtpImgIndex < this.jbtpUrlCandidates.length - 1) {
        this.jbtpImgIndex += 1;
      }
    },
    formatTime(val) {
      if (val == null || val === "") return "—";
      let ms = val;
      if (typeof val === "number") {
        ms = val < 1e12 ? val * 1000 : val;
      } else if (typeof val === "string" && /^\d+$/.test(val.trim())) {
        const n = Number(val.trim());
        ms = n < 1e12 ? n * 1000 : n;
      }
      const d = new Date(ms);
      if (Number.isNaN(d.getTime())) return String(val);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const h = String(d.getHours()).padStart(2, "0");
      const min = String(d.getMinutes()).padStart(2, "0");
      return `${y}/${m}/${day} ${h}:${min}`;
    },
  },
};
</script>

<style scoped>
.report-scroll {
  height: 100%;
  min-height: 480px;
  box-sizing: border-box;
  overflow-y: auto;
}

.report-scroll-end-gap {
  height: 15px;
  width: 100%;
  flex-shrink: 0;
}

.report-card {
  position: relative;
  margin: 97.5px 12px 0;
  padding-top: 20px;
  padding-bottom: 24px;
  background: #fff;
  border-radius: 0;
  /* 裁剪到白卡片内；斜向水印层会略超出边框，由卡片裁切，避免叠到外层 scroll 黑底 */
  overflow: hidden;
}

.report-card-body {
  position: relative;
  z-index: 1;
}

.report-card-header {
  width: 100%;
  box-sizing: border-box;
  background-color: #ffffff;
}

/* 角标绝对定位于标题区右下，不参与标题 flex，不改变顶栏排版 */
.report-title-wrap {
  position: relative;
  width: 100%;
  box-sizing: border-box;
}

.report-title-row {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 14px 8px;
}

/* 透明底红章：无白底贴片/投影，正方形占位 + aspectFit 完整显示 */
.report-tonguo-badge {
  width: 66px;
  height: 66px;
  z-index: 2;
  background: transparent;
  padding: 0;
  border: none;
  border-radius: 0;
  box-shadow: none;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  flex-shrink: 0;
}

/* 顶栏主标题区域右下角（叠在「报告」标题右侧，不占文档流） */
.report-tonguo-badge--header {
  position: absolute;
  right: -5px;
  bottom: -50px;
  width: 104px;
  height: 104px;
  transform: rotate(-20deg);
  transform-origin: center center;
}

.report-tonguo-badge-img {
  width: 66px;
  height: 66px;
  display: block;
}

.report-tonguo-badge--header .report-tonguo-badge-img {
  width: 130px;
  height: 130px;
}

.report-title-inner {
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 100%;
}

.report-title-logo-wrap {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  margin-right: 8px;
  border-radius: 50%;
  overflow: hidden;
  background: #f3f0f7;
}

.report-title-logo {
  width: 100%;
  height: 100%;
  display: block;
}

.report-title {
  flex: 1;
  min-width: 0;
  font-size: 21px;
  font-weight: 700;
  color: #111;
  line-height: 1.35;
  text-align: left;
}

.section {
  padding: 14px 14px 4px;
}

.section-opinion {
  text-align: left;
}

/* 「鉴别意见」与顶栏主文案左缘对齐（与 jb-report-normal 一致） */
.section-opinion .section-label {
  margin-left: -14px;
  padding-left: 16px;
  text-align: left;
}

/* 鉴别意见区块下方横线：左右缩进不与卡片边缘对齐 */
.opinion-section-rule {
  height: 0.5px;
  margin: 6px 14px 0 14px;
  background: #e8e8e8;
}

.section-label {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #111;
  margin-bottom: 8px;
  text-align: left;
}

.section-opinion .opinion-main {
  text-align: center;
}

.opinion-fallback-wrap {
  padding: 4px 0 2px;
}

.opinion-main {
  display: block;
  font-size: 20px;
  font-weight: bold;
  color: #111;
  line-height: 1.6;
}

.opinion-mismatch-reason {
  display: block;
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #111111;
  line-height: 1.55;
  text-align: left;
}

.opinion-mismatch-desc {
  display: block;
  margin-top: 10px;
  font-size: 13px;
  font-weight: 400;
  color: #666666;
  line-height: 1.65;
  text-align: left;
}

.opinion-dual-row {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 10px;
  padding: 4px 0 2px;
}

.opinion-dual-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: stretch;
}

.opinion-dual-bar {
  width: 3px;
  flex-shrink: 0;
  margin-right: 7px;
  border-radius: 1.5px;
  background: #000000;
}

.opinion-dual-body {
  flex: 1;
  min-width: 0;
}

.opinion-dual-label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #111;
  line-height: 1.45;
  margin-bottom: 5px;
}

.opinion-dual-desc {
  display: block;
  font-size: 11px;
  font-weight: 400;
  color: #333;
  line-height: 1.5;
  margin-bottom: 6px;
}

.opinion-dual-verdict {
  display: block;
  font-size: 23px;
  font-weight: 700;
  color: #111;
  line-height: 1.35;
  text-align: left;
}

.section-divider {
  padding-top: 14px;
}

.section-heading {
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: #111;
  margin-bottom: 12px;
}

.sample-info-row {
  display: flex;
  flex-direction: row;
  align-items: stretch;
}

.sample-info-thumb-wrap {
  flex-shrink: 0;
  align-self: stretch;
  width: 100px;
  min-height: 130px;
  margin-right: 12px;
  border-radius: 6px;
  overflow: hidden;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.sample-info-thumb {
  width: 100px;
  height: 100%;
  min-height: 130px;
  display: block;
  object-fit: contain;
  background: #ffffff;
}

.sample-info-thumb--ph {
  width: 100px;
  height: 100%;
  min-height: 130px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #9ca3af;
}

.sample-info-list {
  flex: 1;
  min-width: 0;
}

.sample-info-block {
  margin-bottom: 10px;
}

.sample-info-block:last-child {
  margin-bottom: 0;
}

.sample-info-block--row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
}

.sample-info-block--row .sample-k {
  flex-shrink: 0;
  width: auto;
  margin-bottom: 0;
  margin-right: 2px;
  line-height: 1.45;
  white-space: nowrap;
}

.sample-v-wrap {
  flex: 1;
  min-width: 0;
}

.sample-k {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #111;
}

.sample-v {
  display: block;
  font-size: 13px;
  line-height: 1.45;
  word-break: break-all;
}

.sample-v--muted {
  color: #666666;
  font-weight: 400;
}

.sample-v--sub {
  color: #666666;
  font-size: 12px;
  margin-top: 3px;
  font-weight: 400;
}

/* 样品外观鉴别内容项（参考运营稿：大类标题 + 三列灰字条目） */
.app-checklist-section {
  padding-bottom: 4px;
}

.app-checklist-title {
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: #111111;
  margin-bottom: 7px;
}

.app-checklist-group {
  margin-bottom: 6px;
}

.app-checklist-group--last {
  margin-bottom: 0;
}

.app-checklist-cat {
  display: block;
  font-size: 13px;
  font-weight: 400;
  font-family:
    "Heiti SC",
    "PingFang SC",
    "Microsoft YaHei",
    SimHei,
    sans-serif;
  color: #111111;
  margin-bottom: 3px;
  line-height: 1.35;
}

/* 每条为整块：条内不换行；一行摆不下时整条落到下一行 */
.app-checklist-grid {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
}

.app-checklist-item {
  box-sizing: border-box;
  flex: 0 0 auto;
  padding-right: 16px;
  padding-bottom: 6px;
  font-size: 11px;
  font-weight: 400;
  color: #888888;
  line-height: 1.45;
  white-space: nowrap;
}

.sample-realpicture-section .section-heading {
  margin-bottom: 6px;
}

.sample-realpicture-empty {
  padding: 2px 0 1px;
}

.sample-realpicture-empty-txt {
  font-size: 13px;
  color: #666666;
  line-height: 1.55;
}

.sample-realpicture-scroll-hint {
  margin-top: -5px;
  padding: 0 0 4px;
}

.sample-realpicture-scroll-hint-txt {
  font-size: 12px;
  color: #888888;
  line-height: 1.45;
}

.sample-realpicture-extra {
  margin-top: 8px;
}

/* ≤4 张：均分整行，视觉效果等同「一行四张」单格大小（四列等分） */
.sample-realpicture-row--fit4 {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
}

/*
 * 单格宽 = calc((100% - 18px) / 4)，100% 为 section 内一行宽度。
 * 等价于：750 - report-card 左右 margin 48 - section 左右 padding 56 - 三列间距 36 → (750 - 140) / 4。
 */
.sample-realpicture-cell--fit4 {
  flex: 0 0 calc((100% - 18px) / 4);
  width: calc((100% - 18px) / 4);
  max-width: calc((100% - 18px) / 4);
  height: 84px;
  border-radius: 5px;
  overflow: hidden;
  background: #f3f4f6;
  box-sizing: border-box;
}

/* 横向滑动：单格与 fit4 同源公式，避免固定 82px 与可视区舍入不一致导致第 4 张被裁切 */
.sample-realpicture-scroll {
  width: 100%;
  white-space: nowrap;
  box-sizing: border-box;
  overflow-x: auto;
}

.sample-realpicture-row--scroll {
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 6px;
  padding-bottom: 2px;
  box-sizing: border-box;
}

/*
 * 横向 scroll 内无法用 100% 稳定对齐 section 宽度，故与 fit4 数值对齐：
 * 70px = card 左右 margin 24×2 + section 左右 padding 28×2 + 四列间隔 12×3
 */
.sample-realpicture-cell--fixed {
  flex: 0 0 calc((375px - 70px) / 4);
  width: calc((375px - 70px) / 4);
  min-width: calc((375px - 70px) / 4);
  max-width: calc((375px - 70px) / 4);
  height: 84px;
  border-radius: 5px;
  overflow: hidden;
  background: #f3f4f6;
  box-sizing: border-box;
}

.sample-realpicture-img {
  width: 100%;
  height: 100%;
  display: block;
}

.sample-realpicture-ph {
  width: 100%;
  height: 100%;
  background: #eceef0;
}

.gc-block {
  margin-bottom: 16px;
}

.gc-block--last {
  margin-bottom: 0;
}

.gc-subtitle {
  display: block;
  font-size: 13px;
  font-weight: 400;
  color: #888888;
  margin-bottom: 8px;
  line-height: 1.4;
}

.gc-chart-wrap {
  width: 100%;
  border-radius: 6px;
  overflow: hidden;
  background: #ffffff;
}

.gc-chart-img {
  width: 100%;
  display: block;
}

.gc-chart-secure {
  padding: 18px 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
}

.gc-chart-secure-txt {
  font-size: 13px;
  color: #666666;
  line-height: 1.55;
  text-align: center;
}

.gc-chart-ph {
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: #ffffff;
}

.gc-chart-ph-txt {
  font-size: 12px;
  color: #9ca3af;
}

.report-url-img {
  width: 100%;
  display: block;
}

.report-qianming-wrap {
  padding: 12px 14px 4px;
  box-sizing: border-box;
}

.report-qianming-img {
  width: 100%;
  display: block;
}

.report-tips-section {
  padding-bottom: 16px;
}

.report-tips-title {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #111111;
  letter-spacing: 0.02em;
  margin-bottom: 12px;
}

.report-tips-line {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #333333;
  line-height: 1.65;
  text-align: left;
  margin-bottom: 8px;
}

.report-tips-line--last {
  margin-bottom: 0;
}

.program-cta-banner {
  padding-bottom: 20px;
}

.program-cta-inner {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.program-cta-texts {
  flex: 1;
  min-width: 0;
  margin-right: 12px;
}

.program-cta-title {
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: #111111;
  line-height: 1.4;
  margin-bottom: 8px;
}

.program-cta-desc {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #666666;
  line-height: 1.55;
}

.program-cta-code {
  width: 88px;
  height: 88px;
  flex-shrink: 0;
  border-radius: 50%;
}
</style>



// WEBPACK FOOTER //
// src/components/jbReport/JbReportSecondhand.vue